import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import CommandCenter from "./pages/CommandCenter";
import CoreConnect from "./pages/CoreConnect";
import CoreFund from "./pages/CoreFund";
import ExpertPage from "./pages/ExpertPage";import VirtualCFO from "./pages/VirtualCFO";
import VirtualCEO from "./pages/VirtualCEO";
import VirtualCMO from "./pages/VirtualCMO";
import CampaignPlaybookPage from "./pages/CampaignPlaybookPage";
import VirtualCTO from "./pages/VirtualCTO";
import CompanyProjectile from "./pages/CompanyProjectile";
import Forecasting from "./pages/Forecasting";
import SmartAlerts from "./pages/SmartAlerts";
import BusinessPanels from "./pages/BusinessPanels";
import Benchmarking from "./pages/Benchmarking";
import CAPortal from "./pages/CAPortal";
import AdvancedAI from "./pages/AdvancedAI";
import ComingSoon from "./pages/ComingSoon";
import SignupPage from "./pages/onboarding/SignupPage";
import LoginPage from "./pages/onboarding/LoginPage";
import VerifyEmailPage from "./pages/onboarding/VerifyEmailPage";
import BusinessProfilePage from "./pages/onboarding/BusinessProfilePage";
import SetupWizardPage from "./pages/onboarding/SetupWizardPage";
import WelcomePage from "./pages/onboarding/WelcomePage";
import AuthCallback from "./pages/onboarding/AuthCallback";
import CollectPhonePage from "./pages/onboarding/CollectPhonePage";
import ParamUpdateToaster from "./components/ParamUpdateToaster";
import AICoolingPill from "./components/AICoolingPill";
import useCustomerData from "./hooks/useCustomerData";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ExpertAdmin from "./pages/admin/ExpertAdmin";
import TallyConnectPage from "./pages/TallyConnectPage";
import SharedTaskPage from "./pages/SharedTaskPage";
import SupplierVerification from "./pages/SupplierVerification";
import IntegrationsPage from "./pages/IntegrationsPage";
import FinancialSyncPage from "./pages/FinancialSyncPage";
import TrendsPage from "./pages/TrendsPage";
import StatementHistoryPage from "./pages/StatementHistoryPage";
import ExecutiveBriefingPage from "./pages/ExecutiveBriefingPage";
import AccountPage from "./pages/AccountPage";
import BillingTest from "./pages/BillingTest";
import PlansBilling from "./pages/PlansBilling";
import TrialExpiredBlock from "./components/TrialExpiredBlock";
import { useEntitlement } from "./hooks/EntitlementProvider";
import { api } from "./lib/api";
import { ALL_NAV_ITEMS, findNavItem } from "./lib/nav";
import { useCurrentCustomer, getCurrentCustomer, restoreSession } from "./lib/customerSession";
import { NewCustomerBanner } from "./components/CustomerStateUI";
import "./App.css";

// Modules that don't yet have a dedicated page → render a Coming Soon card.
const COMING_SOON_PATHS = ALL_NAV_ITEMS.filter(
  (i) => !["executive", "cfo", "ceo", "cmo", "cto", "projectile", "forecasting", "alerts", "business_panels", "benchmarking", "ca_portal", "advanced_ai", "integrations", "pricing"].includes(i.id)
);

export default function App() {
  const location = useLocation();
  const [company, setCompany] = useState(null);
  const [supabaseStatus, setSupabaseStatus] = useState({ ok: false, message: "Checking…" });
  // Responsive shell (Phase 1, 2026-02): drives the mobile off-canvas drawer
  // AND the tablet icon-rail↔expanded-overlay toggle from one hamburger in
  // TopBar. Has zero effect at desktop (≥1200px) — Sidebar/TopBar ignore it
  // there via bounded `desktop:` classes, so nothing regresses.
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    // Auto-close the mobile/tablet nav on every route change so a link tap
    // doesn't leave the drawer/overlay open over the new page.
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    api.company().then((res) => {
      if (!cancelled && res.ok) setCompany(res.data);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      const res = await api.supabaseHealth();
      if (cancelled) return;
      if (res.ok) setSupabaseStatus(res.data);
      else setSupabaseStatus({ ok: false, message: res.error });
    };
    ping();
    const id = setInterval(ping, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleCompanyLoaded = useCallback((c) => setCompany(c), []);

  const active = findNavItem(location.pathname);
  // Page title is the active nav item; CFO sub-tabs read their own state internally.
  const pageTitle = active?.title || "C-ORE";

  // Public onboarding + admin pages render WITHOUT the dashboard shell (no sidebar, no top bar).
  const onboardingPaths = ["/signup", "/login", "/verify-email", "/business-profile", "/setup", "/welcome", "/auth/callback", "/collect-phone"];
  const adminPaths      = ["/admin", "/admin/dashboard", "/admin/expert"];
  const publicPaths     = ["/connect/tally", "/shared/task", "/r"];
  const isOnboarding = onboardingPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const isAdmin      = adminPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const isPublic     = publicPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  // Two-state viewer model — see /app/frontend/src/lib/customerSession.js.
  // Demo / visitor mode is removed: anyone without an active customer session
  // is redirected to /login. Hooks must be called unconditionally to keep a
  // stable hook order across renders.
  const { customer, state } = useCurrentCustomer();
  // v1.4.5 — sidebar HEALTH score now reads the calc engine's real
  // per-customer score (was hardcoded to null, so the chip showed
  // "— / 100" even when the backend had a valid number). Safe to call
  // unconditionally: the hook returns null while in-flight.
  const { customerData: liveCustomerData } = useCustomerData();
  // App-wide entitlement gate (backend-enforced; this only mirrors it so
  // protected routes don't each need their own check — see
  // hooks/EntitlementProvider.jsx + backend/entitlement_middleware.py).
  const { status: entitlementStatus } = useEntitlement();

  // 2026-08-21 — persistent-session bootstrap. sessionStorage is empty on a
  // fresh browser session (tab/window reopened), so on FIRST app load we
  // give the server's httpOnly `session_token` cookie one chance to
  // silently restore the customer before deciding whether to bounce to
  // /login. Runs exactly once per full page load (not per route change).
  const [sessionChecked, setSessionChecked] = useState(false);
  useEffect(() => {
    if (getCurrentCustomer() || isOnboarding || isAdmin || isPublic) {
      setSessionChecked(true);
      return;
    }
    restoreSession().finally(() => setSessionChecked(true));
  }, []);

  if (isOnboarding || isAdmin || isPublic) {
    return (
      <Routes>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/business-profile" element={<BusinessProfilePage />} />
        <Route path="/setup" element={<SetupWizardPage />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/collect-phone" element={<CollectPhonePage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/expert" element={<ExpertAdmin />} />
        <Route path="/connect/tally" element={<TallyConnectPage />} />
        <Route path="/shared/task/:token" element={<SharedTaskPage />} />
      </Routes>
    );
  }

  // Session restore still in flight — wait rather than flashing a bounce
  // to /login for a customer who's actually still authenticated server-side.
  if (!sessionChecked) {
    return null;
  }

  // No active customer session → redirect to login. Demo / visitor mode
  // is removed; this app shows real data only.
  // Exception: /account renders its own "Not signed in" empty state
  // (with a Go-to-Sign-in CTA) instead of an opaque bounce, so users
  // who hit /account from a stale bookmark see something readable.
  if (state !== "active" && state !== "new" && location.pathname !== "/account") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // For any logged-in customer (state "active" OR "new"/profile_complete) we
  // override the company name/subtitle so the sidebar + top bar show THEIR
  // business. We also overwrite the tally-synced pill and the date chip so no
  // stale visitor-demo values (e.g. "Your Company", health 74, "16 Apr 2026")
  // ever leak into the chrome. The status gate was dropped for the same reason
  // as api.js activeCustomerId(): "active" is an internal admin flag and has
  // nothing to do with whether a real customer is logged in.
  const displayCompany = (() => {
    if (!customer) return company;
    const subtitle = [customer.industry, customer.city].filter(Boolean).join(" · ") || customer.email || "";
    const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    return {
      ...(company || {}),
      name:                       customer.company_name || customer.name || (company?.name ?? "Loading…"),
      subtitle:                   subtitle || (company?.subtitle ?? ""),
      // v1.4.5 — read the calc-engine health score from the live
      // dashboard payload. Renders "— / 100" until the first fetch
      // resolves; never inherits the demo seed's value.
      health_score:               liveCustomerData?.health?.health_score ?? null,
      tally_synced_minutes_ago:   null,
      current_date:               today,
    };
  })();

  // Billing/Plans, Account, and the dev-only billing test page must always
  // stay reachable so an expired customer can actually reactivate.
  const ENTITLEMENT_EXEMPT_PATHS = ["/pricing", "/account", "/billing-test"];
  const isEntitlementExempt = ENTITLEMENT_EXEMPT_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/")
  );
  const blockForExpiredEntitlement = entitlementStatus === "expired" && !isEntitlementExempt;

  return (
    <div className="min-h-screen bg-[#F5F9FF] overflow-x-hidden">
      <Sidebar company={displayCompany} offsetTop={0} navOpen={navOpen} onClose={() => setNavOpen(false)} />
      <TopBar
        pageTitle={pageTitle}
        company={displayCompany}
        supabaseStatus={supabaseStatus}
        customer={customer}
        offsetTop={0}
        onToggleNav={() => setNavOpen((v) => !v)}
      />

      {state === "new" && (
        <div className="ml-0 tablet:ml-16 desktop:ml-[230px]" style={{ paddingTop: 44 }}>
          <NewCustomerBanner customer={customer} />
        </div>
      )}

      <main
        data-testid="main-content"
        className="ml-0 tablet:ml-16 desktop:ml-[230px] min-h-screen"
        style={{
          position: "relative",
          // New customer: 0 — amber banner wrapper above already clears the top bar
          // Active: 44 to clear the fixed top bar
          paddingTop: state === "new" ? 0 : 44,
        }}
      >
        {blockForExpiredEntitlement ? (
          <TrialExpiredBlock />
        ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/command-center" replace />} />
          <Route
            path="/executive"
            element={
              <ExecutiveDashboard
                supabaseStatus={supabaseStatus}
                onCompanyLoaded={handleCompanyLoaded}
              />
            }
          />
          <Route path="/cfo" element={<VirtualCFO />} />
          <Route path="/command-center" element={<CommandCenter />} />
          <Route path="/connect" element={<CoreConnect />} />
          <Route path="/fund" element={<CoreFund />} />
          <Route path="/expert" element={<ExpertPage />} />
          <Route path="/whatsapp" element={<Navigate to="/connect" replace />} />
          <Route path="/ceo" element={<VirtualCEO />} />
          <Route path="/cmo" element={<VirtualCMO />} />
          <Route path="/cmo/playbook" element={<CampaignPlaybookPage />} />
          <Route path="/cto" element={<VirtualCTO />} />
          <Route path="/projectile" element={<CompanyProjectile />} />
          <Route path="/forecasting" element={<Forecasting />} />
          <Route path="/alerts" element={<SmartAlerts />} />
          <Route path="/business-panels" element={<BusinessPanels />} />
          <Route path="/benchmarking" element={<Benchmarking />} />
          <Route path="/supplier-verification" element={<SupplierVerification />} />
          <Route path="/ca-portal" element={<CAPortal />} />
          <Route path="/advanced-ai" element={<AdvancedAI />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/financial-sync" element={<FinancialSyncPage />} />
          <Route path="/financial-sync/history" element={<StatementHistoryPage />} />
          <Route path="/trends" element={<TrendsPage />} />
          <Route path="/briefing" element={<ExecutiveBriefingPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/pricing" element={<PlansBilling />} />
          {/* DEV/TEST only — hidden from sidebar nav (see lib/nav.js). */}
          <Route path="/billing-test" element={<BillingTest />} />

          {/* Legacy deep-links → CFO sub-tabs */}
          <Route path="/inventory" element={<Navigate to="/cfo#inventory" replace />} />
          <Route path="/ccc"       element={<Navigate to="/cfo#ccc"       replace />} />
          <Route path="/margin"    element={<Navigate to="/cfo#margins"   replace />} />

          {COMING_SOON_PATHS.map((item) => (
            <Route
              key={item.id}
              path={item.path}
              element={<ComingSoon title={item.title} />}
            />
          ))}
          <Route
            path="*"
            element={
              <div className="px-6 py-10 text-center text-[13px] text-slate-500">
                We couldn&apos;t find that page. Use the sidebar to pick a module.
              </div>
            }
          />
        </Routes>
        )}
      </main>

      {/* Global toaster + ripple for inline-edit saves. */}
      <ParamUpdateToaster />

      {/* Soft "AI cooling down…" pill — only visible while GLM is rate-limited. */}
      <AICoolingPill />
    </div>
  );
}
